/**
 * Main App Component
 * PAO Management System v2.0
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { ErrorBoundary } from './components/ErrorBoundary'
import { ProtectedRoute } from './components/ProtectedRoute'
import { SessionManager } from './components/SessionManager'
import { Layout } from './components/layout/Layout'
import { Login } from './pages/Login'
import { PasswordExpired } from './pages/PasswordExpired'
import { Dashboard } from './pages/Dashboard'
import { NotFound } from './pages/NotFound'
import ClientList from './pages/clients/ClientList'
import ClientForm from './pages/clients/ClientForm'
import ClientDetail from './pages/clients/ClientDetail'
import VisitList from './pages/visits/VisitList'
import VisitForm from './pages/visits/VisitForm'
import VisitDetail from './pages/visits/VisitDetail'
import VisitWizard from './pages/visits/wizard/VisitWizard'
import CaseList from './pages/cases/CaseList'
import CaseForm from './pages/cases/CaseForm'
import CaseDetail from './pages/cases/CaseDetail'
import ReportsMenu from './pages/reports/ReportsMenu'
import ReportGenerate from './pages/reports/ReportGenerate'
import UserManagement from './pages/admin/UserManagement'
import OfficeManagement from './pages/admin/OfficeManagement'
import CourtManagement from './pages/admin/CourtManagement'
import ProfilePage from './pages/profile/ProfilePage'

function AppRoutes() {
  const { isAuthenticated } = useAuth()

  return (
    <>
      <SessionManager />
      <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          isAuthenticated ? <Navigate to="/" replace /> : <Login />
        }
      />

      {/* Forced password-expiry gate - authenticated, but no dashboard chrome */}
      <Route
        path="/password-expired"
        element={
          <ProtectedRoute>
            <PasswordExpired />
          </ProtectedRoute>
        }
      />

      {/* Protected Routes with Layout */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Client Routes */}
      <Route
        path="/clients"
        element={
          <ProtectedRoute>
            <Layout>
              <ClientList />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/new"
        element={
          <ProtectedRoute>
            <Layout>
              <ClientForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <ClientDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/clients/:id/edit"
        element={
          <ProtectedRoute>
            <Layout>
              <ClientForm />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Visit Routes - Admin & Lawyer Only */}
      <Route
        path="/visits"
        element={
          <ProtectedRoute requiredRoles={["admin", "lawyer"]}>
            <Layout>
              <VisitList />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/visits/wizard"
        element={
          <ProtectedRoute requiredRoles={["admin", "lawyer"]}>
            <VisitWizard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/visits/:id"
        element={
          <ProtectedRoute requiredRoles={["admin", "lawyer"]}>
            <Layout>
              <VisitDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/visits/:id/edit"
        element={
          <ProtectedRoute requiredRoles={["admin", "lawyer"]}>
            <Layout>
              <VisitForm />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Case Routes */}
      <Route
        path="/cases"
        element={
          <ProtectedRoute>
            <Layout>
              <CaseList />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cases/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <CaseDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cases/:id/edit"
        element={
          <ProtectedRoute>
            <Layout>
              <CaseForm />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Report Routes - Admin & Lawyer Only */}
      <Route
        path="/reports"
        element={
          <ProtectedRoute requiredRoles={["admin", "lawyer"]}>
            <Layout>
              <ReportsMenu />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Admin Routes - Admin Only */}
      <Route path="/admin" element={<Navigate to="/admin/users" replace />} />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute requiredRoles={["admin"]}>
            <Layout>
              <UserManagement />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/offices"
        element={
          <ProtectedRoute requiredRoles={["admin"]}>
            <Layout>
              <OfficeManagement />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/courts"
        element={
          <ProtectedRoute requiredRoles={["admin"]}>
            <Layout>
              <CourtManagement />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Profile Route - All Authenticated Users */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Layout>
              <ProfilePage />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* 404 Not Found */}
      <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  )
}

export default App
